import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import type { UnifiedLocus, UnifiedGene } from './types';
import { LocusGeneContextMenu } from './components/LocusGeneContextMenu';
import { analysisIdAtom } from '../sharedState';
import { shouldHandleSemanticLinkClick } from '../navigationUrl';
import { getContainingLocusRegionId } from './overviewVariantNavigation';
import {
  geneHasDisplayedPhenotypeEvidence,
  getPhenotypeLocusNavigationDecision,
  getSignificantBurdenTypes,
  isBurdenOnlyLocus,
  type PhenotypeLocusNavigationDecision,
} from './locusNavigationPolicy';
import {
  AVAILABLE_TABLE_BOTTOM_PADDING,
  AVAILABLE_TABLE_MIN_VIEWPORT_HEIGHT,
  useAvailableTableHeight,
} from '../availableTableHeight';
import './ManhattanViewer.css';


export interface UnifiedLocusTableProps {
  /** Unified loci from the overview API */
  unifiedLoci: UnifiedLocus[];
  /** Callback when a locus is clicked (for zoom navigation) */
  onLocusClick?: (contig: string, position: number, start?: number, stop?: number) => void;
  /** Exact phenotype-aware destination selected by the shared locus policy. */
  onNavigate?: (decision: PhenotypeLocusNavigationDecision) => void;
  /** Analysis retained by all table navigation decisions. */
  analysisId: string;
  /** Same-tab semantic navigation for an ordinary coding-variant click. */
  onVariantClick?: (variantId: string, geneId: string, regionId: string) => void;
  /** Canonical href retained for context-menu and modified-click new tabs. */
  getVariantHref?: (variantId: string, geneId: string, locus: UnifiedLocus) => string;
  /** Set of selected peak IDs for custom labeling */
  selectedPeakIds: Set<string>;
  /** Callback to toggle a peak selection - passes filtered loci for initialization */
  onTogglePeak: (peakId: string, currentLabeledIds?: Set<string>) => void;
  /** Whether in custom label mode */
  customLabelMode: boolean;
  /** Number of top peaks to label in default mode */
  topN: number;
  /** Whether to hide singleton loci (controlled by parent) */
  hideSingletons: boolean;
  /** Callback to toggle singleton filter */
  onSetHideSingletons: (v: boolean) => void;
  /** Set number of top peaks to label (exits custom mode) */
  onSetTopN: (n: number) => void;
  /** Clear all selections */
  onClearSelection: () => void;
  /** Reset to default mode */
  onResetToDefault: () => void;
  /** Minimum significant variants for non-coding loci */
  minNonCodingVariants: number;
  /** Callback to set min non-coding variants filter */
  onSetMinNonCodingVariants: (v: number) => void;
}

/**
 * Get total coding variant counts for a gene (genome + exome)
 */
function getGeneCodingCounts(g: UnifiedGene): { lof: number; missense: number } {
  const lof = (g.genome_coding_hits?.lof ?? 0) + (g.exome_coding_hits?.lof ?? 0);
  const missense = (g.genome_coding_hits?.missense ?? 0) + (g.exome_coding_hits?.missense ?? 0);
  return { lof, missense };
}

/**
 * Unified Locus Table Component.
 *
 * Displays merged evidence from genome GWAS, exome GWAS, and gene burden tests.
 */
export const UnifiedLocusTable: React.FC<UnifiedLocusTableProps> = ({
  unifiedLoci,
  onLocusClick,
  onNavigate,
  analysisId,
  onVariantClick,
  getVariantHref,
  selectedPeakIds,
  onTogglePeak,
  customLabelMode,
  topN,
  onSetTopN,
  onClearSelection,
  onResetToDefault,
  hideSingletons,
  onSetHideSingletons,
  minNonCodingVariants,
  onSetMinNonCodingVariants,
}) => {
  const [showOnlyImplicated, setShowOnlyImplicated] = useState(false);
  const [visibleRowCount, setVisibleRowCount] = useState(100);
  const [searchText, setSearchText] = useState('');
  const currentAnalysisId = useRecoilValue(analysisIdAtom);
  // Local slider value with debounced propagation to avoid layout thrash
  const [sliderValue, setSliderValue] = useState(topN);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => { setSliderValue(topN); }, [topN]);
  // Unified context menu state - can include locus, gene, or both
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    locus?: { contig: string; position: number; start?: number; stop?: number };
    gene?: { geneId: string; geneSymbol: string; relationship: 'implicated' | 'nearby' };
  } | null>(null);

  // Sort by implicated first, then best p-value
  const sortedLoci = useMemo(() => {
    return [...unifiedLoci].sort((a, b) => {
      const aImpl = a.genes.some(geneHasDisplayedPhenotypeEvidence);
      const bImpl = b.genes.some(geneHasDisplayedPhenotypeEvidence);
      if (aImpl !== bImpl) return aImpl ? -1 : 1;
      const bestA = Math.min(a.pvalue_genome ?? Infinity, a.pvalue_exome ?? Infinity);
      const bestB = Math.min(b.pvalue_genome ?? Infinity, b.pvalue_exome ?? Infinity);
      return bestA - bestB;
    });
  }, [unifiedLoci]);

  // Filter by search text - matches gene_symbol, contig:position, or special keywords
  const searchFilteredLoci = useMemo(() => {
    if (!searchText.trim()) return sortedLoci;
    const term = searchText.trim().toLowerCase();

    return sortedLoci.filter((locus) => {
      // Check locus position (e.g., "chr1:12345" or just "chr1")
      const locusStr = `${locus.contig}:${locus.position}`.toLowerCase();
      if (locusStr.includes(term) || locus.contig.toLowerCase().includes(term)) {
        return true;
      }

      // Check gene symbols
      if (locus.genes.some((g) => g.gene_symbol.toLowerCase().includes(term))) {
        return true;
      }

      // Special keywords: "lof" matches loci with significant LoF variants/burden
      if (term === 'lof' || term === 'plof') {
        return locus.genes.some((g) => {
          const coding = getGeneCodingCounts(g);
          const hasLofBurden = getSignificantBurdenTypes(g).includes('pLoF');
          return coding.lof > 0 || hasLofBurden;
        });
      }

      // Special keywords: "missense" or "mis" matches loci with missense variants/burden
      if (term === 'missense' || term === 'mis') {
        return locus.genes.some((g) => {
          const coding = getGeneCodingCounts(g);
          const hasMisBurden = getSignificantBurdenTypes(g).includes('missenseLC');
          return coding.missense > 0 || hasMisBurden;
        });
      }

      // Special keywords: "coding" matches loci with any coding variants
      if (term === 'coding') {
        return locus.genes.some((g) => {
          const coding = getGeneCodingCounts(g);
          return coding.lof > 0 || coding.missense > 0;
        });
      }

      // Special keywords: "burden" matches loci with any significant burden test
      if (term === 'burden') {
        return locus.genes.some((g) => getSignificantBurdenTypes(g).length > 0);
      }

      return false;
    });
  }, [sortedLoci, searchText]);

  // Filter to only loci with gene evidence (singleton filtering is handled by parent)
  const filteredLoci = useMemo(() => {
    if (!showOnlyImplicated) return searchFilteredLoci;
    return searchFilteredLoci.filter((locus) => locus.genes.some(geneHasDisplayedPhenotypeEvidence));
  }, [searchFilteredLoci, showOnlyImplicated]);

  const handleSliderChange = useCallback((v: number) => {
    setSliderValue(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSetTopN(v);
    }, 250);
  }, [onSetTopN]);

  const handleSelectAllFiltered = useCallback(() => {
    onSetTopN(filteredLoci.length);
  }, [filteredLoci, onSetTopN]);

  const showMoreRows = useCallback(() => {
    setVisibleRowCount((prev) => prev + 100);
  }, []);

  const loadedRowCount = Math.min(filteredLoci.length, visibleRowCount);
  const { ref: tableViewportRef, policy: tableHeightPolicy } = useAvailableTableHeight<HTMLDivElement>({
    rowCount: loadedRowCount,
    rowHeight: 32,
    headerHeight: 35,
  });

  return (
    <div
      ref={tableViewportRef}
      className="manhattan-table-container"
      style={{
        maxHeight: Math.max(tableHeightPolicy.availableHeight, AVAILABLE_TABLE_MIN_VIEWPORT_HEIGHT),
        marginBottom: tableHeightPolicy.reserveHeight + AVAILABLE_TABLE_BOTTOM_PADDING,
      }}
      data-available-height-table="unified-loci"
      data-available-height-capacity={tableHeightPolicy.capacity}
      data-available-height-reserve={tableHeightPolicy.reserveHeight}
    >
      {/* Control panel above table */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          padding: '8px 12px',
          background: 'var(--theme-surface-alt)',
          borderRadius: 4,
          fontSize: 12,
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Search input */}
          <input
            type="text"
            placeholder="Search genes, loci, lof, missense..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              padding: '4px 8px',
              fontSize: 11,
              border: '1px solid var(--theme-border, #ccc)',
              borderRadius: 3,
              width: 180,
              background: 'var(--theme-surface, #fff)',
              color: 'var(--theme-text, #333)',
            }}
          />
          <span style={{ color: 'var(--theme-text)' }}>
            <strong>{filteredLoci.length}</strong>
            {(showOnlyImplicated || searchText) ? ` / ${sortedLoci.length}` : ''} loci
          </span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginLeft: 8 }}>
            <input
              type="checkbox"
              checked={showOnlyImplicated}
              onChange={(e) => setShowOnlyImplicated(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: 11 }}>Gene implicated</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--theme-text-muted)', whiteSpace: 'nowrap' }} title="Minimum significant variants for non-coding loci (loci with coding variants are always shown)">Min non-coding:</span>
            <input
              type="number"
              min={0}
              value={minNonCodingVariants || ''}
              onChange={(e) => onSetMinNonCodingVariants(Number(e.target.value) || 0)}
              placeholder="0"
              style={{
                width: 40,
                fontSize: 11,
                padding: '2px 4px',
                border: '1px solid var(--theme-border, #ccc)',
                borderRadius: 3,
                background: 'var(--theme-surface, #fff)',
                color: 'var(--theme-text, #333)',
                textAlign: 'center',
              }}
            />
          </label>
          {/* Label controls */}
          {customLabelMode ? (
            <span style={{ color: 'var(--theme-primary, #262262)' }}>
              <strong>{selectedPeakIds.size}</strong> labeled
              <button
                onClick={onResetToDefault}
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  color: 'var(--theme-primary, #262262)',
                  textDecoration: 'underline',
                  marginLeft: 4,
                }}
                title="Return to top-N auto-label mode"
              >
                reset
              </button>
            </span>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--theme-text-muted)', whiteSpace: 'nowrap' }}>Labels:</span>
              <input
                type="range"
                min={0}
                max={Math.min(filteredLoci.length, 100)}
                value={sliderValue}
                onChange={(e) => handleSliderChange(Number(e.target.value))}
                style={{ width: 80, cursor: 'pointer' }}
                title={`Label top ${sliderValue} peaks`}
              />
              <input
                type="number"
                min={0}
                max={filteredLoci.length}
                value={sliderValue}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (!isNaN(v) && v >= 0) handleSliderChange(v);
                }}
                style={{
                  width: 40,
                  fontSize: 11,
                  padding: '2px 4px',
                  border: '1px solid var(--theme-border, #ccc)',
                  borderRadius: 3,
                  background: 'var(--theme-surface, #fff)',
                  color: 'var(--theme-text, #333)',
                  textAlign: 'center',
                }}
              />
              <button
                onClick={handleSelectAllFiltered}
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  cursor: 'pointer',
                  background: 'var(--theme-surface)',
                  border: '1px solid var(--theme-border)',
                  borderRadius: 3,
                  whiteSpace: 'nowrap',
                }}
                title="Label all filtered loci"
              >
                All
              </button>
              <button
                onClick={onClearSelection}
                style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  cursor: 'pointer',
                  background: 'var(--theme-surface)',
                  border: '1px solid var(--theme-border)',
                  borderRadius: 3,
                  whiteSpace: 'nowrap',
                }}
                title="Remove all labels"
              >
                None
              </button>
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Right-click hint */}
          <span style={{ fontSize: 10, color: 'var(--theme-text-muted)', fontStyle: 'italic' }}>
            Right-click rows for options
          </span>
        </div>
      </div>

      <table className="manhattan-table manhattan-table-locus">
        <thead>
          <tr>
            <th style={{ width: 32 }}>Label</th>
            <th>Locus</th>
            <th>Genes in Locus</th>
            <th>P-value (Genome)</th>
            <th>P-value (Exome)</th>
            <th title="Gene burden test results">P-value (Burden)</th>
            <th title="Genome-wide significant variants in locus">Sig. Variants</th>
          </tr>
        </thead>
        <tbody>
          {filteredLoci.slice(0, visibleRowCount).map((locus, index) => {
            const locusId = `${locus.contig}-${locus.position}`;
            const isSelected = selectedPeakIds.has(locusId);
            const hasLabel = customLabelMode ? isSelected : index < topN;

            // Partition genes: implicated first, then non-implicated
            const implicatedGenes = locus.genes.filter(geneHasDisplayedPhenotypeEvidence);
            const nonImplicatedGenes = locus.genes.filter((g) => !geneHasDisplayedPhenotypeEvidence(g));

            // Best burden p-value across all genes
            const bestBurdenPvalue = locus.genes.reduce((best, g) => {
              const types = getSignificantBurdenTypes(g);
              if (types.length === 0) return best;
              // Find best p-value across all burden results
              let minP = Infinity;
              for (const b of g.burden_results || []) {
                if (b.pvalue && b.pvalue < minP) minP = b.pvalue;
                if (b.pvalue_burden && b.pvalue_burden < minP) minP = b.pvalue_burden;
                if (b.pvalue_skat && b.pvalue_skat < minP) minP = b.pvalue_skat;
              }
              return minP < best ? minP : best;
            }, Infinity);

            // Check if this is a burden-only locus (no GWAS signal)
            const isBurdenOnly = isBurdenOnlyLocus(locus);
            const locusDecision = getPhenotypeLocusNavigationDecision({
              analysisId,
              locus,
              interaction: { kind: 'locus' },
            });

            return (
              <tr
                key={locusId}
                className={isSelected ? 'manhattan-row-selected' : ''}
                style={isBurdenOnly ? { background: 'rgba(156, 39, 176, 0.06)' } : undefined}
              >
                <td style={{ width: 32, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={hasLabel}
                    onChange={() => onTogglePeak(locusId)}
                    title={hasLabel ? 'Remove label' : 'Add label'}
                    style={{ cursor: 'pointer' }}
                  />
                </td>
                <td
                  role="link"
                  tabIndex={0}
                  onClick={() => onNavigate?.(locusDecision)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onNavigate?.(locusDecision);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({
                      x: e.clientX,
                      y: e.clientY,
                      locus: { contig: locus.contig, position: locus.position, start: locus.start, stop: locus.stop },
                    });
                  }}
                  style={{ cursor: onNavigate ? 'pointer' : 'default' }}
                  aria-label={locusDecision.destinationLabel}
                  title={`${locusDecision.tooltip} Right-click for more options.`}
                >
                  <span style={{ color: 'var(--theme-primary, #262262)', textDecoration: 'underline' }}>
                    {locus.contig}:{locus.position.toLocaleString()}
                  </span>
                  {isBurdenOnly && (
                    <span
                      style={{
                        fontSize: 9,
                        marginLeft: 6,
                        padding: '1px 5px',
                        background: 'rgba(156, 39, 176, 0.15)',
                        color: '#7b1fa2',
                        borderRadius: 3,
                        fontWeight: 500,
                      }}
                    >
                      Burden only
                    </span>
                  )}
                </td>
                <td>
                  {/* Implicated genes shown first - ALL of them, never truncated */}
                  {implicatedGenes.map((g, i) => {
                    const burdenTypes = getSignificantBurdenTypes(g);
                    const coding = getGeneCodingCounts(g);
                    const geneDecision = getPhenotypeLocusNavigationDecision({
                      analysisId,
                      locus,
                      interaction: { kind: 'gene', geneId: g.gene_id },
                    });
                    const hasBurden = burdenTypes.length > 0;
                    const hasCoding = coding.lof > 0 || coding.missense > 0;

                    return (
                      <span
                        key={g.gene_id}
                        style={{
                          display: 'inline-block',
                          marginRight: 8,
                          marginBottom: 2,
                        }}
                      >
                        <span
                          style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--theme-primary, #262262)' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigate?.(geneDecision);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                              onNavigate?.(geneDecision);
                            }
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setContextMenu({
                              x: e.clientX,
                              y: e.clientY,
                              locus: { contig: locus.contig, position: locus.position, start: locus.start, stop: locus.stop },
                              gene: { geneId: g.gene_id, geneSymbol: g.gene_symbol, relationship: 'implicated' },
                            });
                          }}
                          role="link"
                          tabIndex={0}
                          aria-label={geneDecision.destinationLabel}
                          title={`${geneDecision.tooltip} Right-click for more options.`}
                        >
                          {g.gene_symbol}
                        </span>
                        {/* Burden dots - one per annotation type */}
                        {burdenTypes.includes('pLoF') && (
                          <span style={{ color: '#d32f2f', marginLeft: 2 }} title="pLoF burden">●</span>
                        )}
                        {burdenTypes.includes('missenseLC') && (
                          <span style={{ color: '#e68a00', marginLeft: 1 }} title="Missense burden">●</span>
                        )}
                        {burdenTypes.includes('synonymous') && (
                          <span style={{ color: '#388e3c', marginLeft: 1 }} title="Synonymous burden">●</span>
                        )}
                        {/* Specific Coding Variant details or generic counts fallback */}
                        {hasCoding && g.best_coding_hgvsp && (() => {
                          const isLof = ['stop_gained', 'frameshift_variant', 'splice_acceptor_variant', 'splice_donor_variant', 'start_lost', 'stop_lost'].includes(g.best_coding_csq ?? '');
                          const color = isLof ? '#c62828' : '#e68a00';
                          const variantId = g.best_coding_variant_id;
                          const totalCoding = coding.lof + coding.missense;
                          const moreCount = totalCoding > 1 ? totalCoding - 1 : 0;
                          return (
                            <>
                              <a
                                href={variantId ? getVariantHref?.(variantId, g.gene_id, locus) : undefined}
                                style={{ fontSize: 10, marginLeft: 6, color, fontWeight: 600, cursor: variantId ? 'pointer' : undefined, textDecoration: variantId ? 'underline dotted' : undefined, textUnderlineOffset: '2px' }}
                                onClick={variantId ? (e) => {
                                  e.stopPropagation();
                                  if (!shouldHandleSemanticLinkClick(e)) return;
                                  e.preventDefault();
                                  onVariantClick?.(
                                    variantId,
                                    g.gene_id,
                                    getContainingLocusRegionId(locus)
                                  );
                                } : undefined}
                                title={variantId ? `${variantId} — open Variant PheWAS (Ctrl/Cmd-click or context menu opens a new tab)` : undefined}
                              >
                                {g.best_coding_hgvsp}
                                {g.best_coding_pvalue ? ` P=${g.best_coding_pvalue < 0.001 ? g.best_coding_pvalue.toExponential(1) : g.best_coding_pvalue.toPrecision(2)}` : ''}
                                {g.best_coding_beta !== undefined && <span style={{ color: g.best_coding_beta > 0 ? '#2e7d32' : '#c62828' }}>{g.best_coding_beta > 0 ? ' ↑' : ' ↓'}</span>}
                                {g.best_coding_ac ? ` AC:${g.best_coding_ac}` : ''}
                              </a>
                              {moreCount > 0 && (
                                <span style={{ fontSize: 10, marginLeft: 4, color: 'var(--theme-text-muted, #888)' }}>+{moreCount} more</span>
                              )}
                            </>
                          );
                        })()}
                        {hasCoding && !g.best_coding_hgvsp && (
                          <span style={{ fontSize: 10, marginLeft: 2, color: '#262262', fontWeight: 500 }}>
                            {coding.lof > 0 && (
                              <span>({coding.lof}LOF)</span>
                            )}
                            {coding.missense > 0 && (
                              <span style={{ marginLeft: coding.lof > 0 ? 2 : 0 }}>({coding.missense}MIS)</span>
                            )}
                          </span>
                        )}
                      </span>
                    );
                  })}
                  {/* Non-implicated genes condensed */}
                  {nonImplicatedGenes.length > 0 && (
                    <span style={{ color: 'var(--theme-text-muted, #888)', fontSize: 11 }}>
                      {nonImplicatedGenes.slice(0, 3).map((g, idx) => {
                        const geneDecision = getPhenotypeLocusNavigationDecision({
                          analysisId,
                          locus,
                          interaction: { kind: 'gene', geneId: g.gene_id },
                        });
                        return (
                        <React.Fragment key={g.gene_id}>
                          <span
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onNavigate?.(geneDecision);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.stopPropagation();
                                onNavigate?.(geneDecision);
                              }
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setContextMenu({
                                x: e.clientX,
                                y: e.clientY,
                                locus: { contig: locus.contig, position: locus.position, start: locus.start, stop: locus.stop },
                                gene: { geneId: g.gene_id, geneSymbol: g.gene_symbol, relationship: 'nearby' },
                              });
                            }}
                            role="link"
                            tabIndex={0}
                            aria-label={geneDecision.destinationLabel}
                            title={`${geneDecision.tooltip} Right-click for more options.`}
                          >
                            {g.gene_symbol}
                          </span>
                          {idx < 2 && idx < nonImplicatedGenes.length - 1 && ', '}
                        </React.Fragment>
                        );
                      })}
                      {nonImplicatedGenes.length > 3 && ` +${nonImplicatedGenes.length - 3}`}
                    </span>
                  )}
                  {locus.genes.length === 0 && '—'}
                </td>
                <td>{locus.pvalue_genome?.toExponential(2) ?? '—'}</td>
                <td>{locus.pvalue_exome?.toExponential(2) ?? '—'}</td>
                <td>
                  {bestBurdenPvalue !== Infinity ? bestBurdenPvalue.toExponential(2) : '—'}
                </td>
                <td style={{ textAlign: 'center' }}>{locus.sig_variant_count || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filteredLoci.length > visibleRowCount && (
        <button
          onClick={showMoreRows}
          style={{
            width: '100%',
            padding: '8px',
            marginTop: 4,
            cursor: 'pointer',
            fontSize: 12,
            background: 'var(--theme-surface-alt)',
            border: '1px solid var(--theme-border)',
            borderRadius: 4,
          }}
        >
          Show more ({filteredLoci.length - visibleRowCount} remaining)
        </button>
      )}

      {/* Unified context menu */}
      {contextMenu && (
        <LocusGeneContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          locus={contextMenu.locus}
          gene={contextMenu.gene}
          currentPhenotypeDescription={currentAnalysisId || undefined}
          onClose={() => setContextMenu(null)}
          onLocusClick={onLocusClick}
        />
      )}
    </div>
  );
};

export default UnifiedLocusTable;
